import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';
import { countries, getFlagUrl } from './countries';
import type { Country } from './countries';

export interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  className?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean;
  disabled?: boolean;
}

export function PhoneInput({
  value,
  onChange,
  onBlur,
  className = '',
  placeholder = '123 456 7890',
  required = false,
  error = false,
  disabled = false,
}: PhoneInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries[0]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Tracks the last value this component emitted via onChange, so we can distinguish
  // internal changes (user typing) from external ones (form reset / data load).
  const internalValueRef = useRef<string>('');

  const filteredCountries = searchQuery.trim()
    ? countries.filter(
        (c) =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.dialCode.includes(searchQuery) ||
          c.code.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : countries;

  const formatPhoneNumber = (val: string): string => {
    const digits = val.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
  };

  const toStoredValue = (dialCode: string, digits: string): string =>
    digits ? dialCode + digits.replace(/\D/g, '') : '';

  // Sync internal state whenever the external value changes (e.g. form reset after data loads).
  // We skip the update when value === internalValueRef to avoid interfering with user typing.
  useEffect(() => {
    if (value === internalValueRef.current) return;
    internalValueRef.current = value;

    if (!value) {
      setPhoneNumber('');
      setSelectedCountry(countries[0]);
      return;
    }

    const clean = value.replace(/\s/g, '');
    const matchingCountry = countries.find((c) => clean.startsWith(c.dialCode));
    if (matchingCountry) {
      setSelectedCountry(matchingCountry);
      const numberWithoutCode = clean.slice(matchingCountry.dialCode.length).replace(/\D/g, '');
      setPhoneNumber(formatPhoneNumber(numberWithoutCode));
    } else {
      setPhoneNumber(formatPhoneNumber(value.replace(/\D/g, '')));
    }
  }, [value]);

  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({ top: rect.bottom + 4, left: rect.left });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      setSearchQuery('');
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, '');
    const formatted = formatPhoneNumber(digitsOnly);
    setPhoneNumber(formatted);
    const stored = toStoredValue(selectedCountry.dialCode, digitsOnly);
    internalValueRef.current = stored;
    onChange(stored);
  };

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    const digits = phoneNumber.replace(/\D/g, '');
    const stored = toStoredValue(country.dialCode, digits);
    internalValueRef.current = stored;
    onChange(stored);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const baseInputClass = `flex items-center w-full h-10 px-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50 ${error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300'} ${className}`;

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] flex flex-col"
      style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: 260, maxHeight: 300 }}
    >
      <div className="p-2 border-b border-gray-100 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search country or code..."
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
      </div>
      <div className="overflow-y-auto flex-1 p-1">
        {filteredCountries.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-4">No countries found</p>
        ) : (
          filteredCountries.map((country) => (
            <button
              key={country.code}
              type="button"
              onClick={() => handleCountrySelect(country)}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded hover:bg-gray-100 transition-colors ${selectedCountry.code === country.code ? 'bg-emerald-50' : ''}`}
            >
              <img src={getFlagUrl(country.code)} alt={country.name} className="w-6 h-4 object-cover rounded flex-shrink-0" />
              <span className="flex-1 text-left text-sm text-gray-700">{country.name}</span>
              <span className="text-xs text-gray-500 font-mono">{country.dialCode}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div className="relative">
      <div className={baseInputClass}>
        <div className="flex-shrink-0">
          <button
            ref={triggerRef}
            type="button"
            onClick={() => {
              if (!disabled) {
                setIsOpen(!isOpen);
                if (!isOpen) updatePosition();
              }
            }}
            disabled={disabled}
            className="flex items-center space-x-2 h-full px-3 border-r border-gray-300 hover:bg-gray-50 rounded-l transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <img
              src={getFlagUrl(selectedCountry.code)}
              alt={selectedCountry.name}
              className="w-5 h-4 object-cover rounded"
            />
            <span className="text-sm font-medium text-gray-700">{selectedCountry.dialCode}</span>
            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
        <input
          ref={inputRef}
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneChange}
          onBlur={onBlur}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className="flex-1 border-none outline-none bg-transparent px-2 min-w-0"
          maxLength={14}
        />
      </div>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </div>
  );
}
